import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { DeepReadonly } from '@tradecafe/types/utils'
import { difference } from 'lodash-es'
import { from } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { DocumentApiService } from 'src/api/document-lib/document'
import { DocumentSetApiService } from 'src/api/document-lib/document-set'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { createDocumentTemplate, createDocumentTemplateFailure, createDocumentTemplateSuccess, deleteDocumentTemplate, deleteDocumentTemplateFailure, deleteDocumentTemplateSuccess, loadDocumentTemplates, loadDocumentTemplatesFailure, loadDocumentTemplatesSuccess, updateDocumentTemplate, updateDocumentTemplateFailure, updateDocumentTemplateSuccess } from './document-template.actions'

@Injectable()
export class DocumentTemplateEffects {
  private actions$ = inject(Actions)
  private DocumentApi = inject(DocumentApiService)
  private DocumentSetApi = inject(DocumentSetApiService)
  private toaster = inject(ToasterService)


  loadDocumentTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(loadDocumentTemplates),
    switchMap(action =>
      from(this.DocumentApi.list()).pipe(
        map(r => loadDocumentTemplatesSuccess({ tableKey: action.tableKey, templates: r.data })),
        catchAndLog(this.toaster, 'Unable to fetch document templates', loadDocumentTemplatesFailure
      )))))

  createDocumentTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(createDocumentTemplate),
    tap(() => this.toaster.progress('Creating document template...')),
    switchMap(action =>
      from(this.DocumentApi.create(action.template).then(({ data }) =>
        this.updateDocumentSetsForDocument(data.document_id, action.template.doc_set, []).then(() => data))
      ).pipe(
        map(r => createDocumentTemplateSuccess({ template: r })),
        tap(() => this.toaster.success('Document created successfully.')),
        catchAndLog(this.toaster, 'Unable to create document template', createDocumentTemplateFailure
      )))))

  updateDocumentTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(updateDocumentTemplate),
    tap(() => this.toaster.progress('Updating document template...')),
    switchMap(action =>
      from(Promise.all([
        this.DocumentApi.update(action.id, action.template),
        this.updateDocumentSetsForDocument(action.id, action.template.doc_set, action.original.doc_set),
      ])).pipe(
        map(([r]) => updateDocumentTemplateSuccess({ template: r.data })),
        tap(() => this.toaster.success('Document updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update document template', updateDocumentTemplateFailure
      )))))

  deleteDocumentTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(deleteDocumentTemplate),
    tap(() => this.toaster.progress('Deleting document template...')),
    switchMap(action =>
      this.DocumentApi.delete(action.template.document_id).pipe(
        map(() => deleteDocumentTemplateSuccess({ id: action.id })),
        tap(() => this.toaster.success('Document deleted successfully.')),
        catchAndLog(this.toaster, 'Unable to delete document template', deleteDocumentTemplateFailure
      )))))

  private updateDocumentSetsForDocument(documentId: string, newSets: DeepReadonly<string[]> = [], oldSets: DeepReadonly<string[]> = []) {
    const added = difference(newSets, oldSets)
    const removed = difference(oldSets, newSets)

    return Promise.all([
      ...added.map(setId => this.DocumentSetApi.addDoc(setId, documentId)),
      ...removed.map(setId => this.DocumentSetApi.removeDoc(setId, documentId)),
    ])
  }
}
